import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { LedgerEventDialog } from '@/components/ledger/LedgerEventDialog';
import { LedgerEventList } from '@/components/ledger/LedgerEventList';
import { FundingRoundCard } from '@/components/ledger/FundingRoundCard';
import { ScenarioWorkbench } from '@/components/ledger/ScenarioWorkbench';
import TransferDutyPanel from '@/components/ledger/TransferDutyPanel';
import { useToast } from '@/hooks/use-toast';
import { useLedgerStream } from '@/hooks/useLedgerStream';
import { useTenantStore } from '@/hooks/useTenantStore';
import { supabase } from '@/integrations/supabase/client';
import * as CapLedgerService from '@/services/capLedgerService';

const Transactions = () => {
  const { toast } = useToast();
  const { tenantId } = useTenantStore();
  const [events, setEvents] = useState<CapLedgerService.LedgerEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [companyJurisdiction, setCompanyJurisdiction] = useState<string>('US');
  const ledgerStream = useLedgerStream();

  useEffect(() => {
    fetchEvents();
    if (tenantId) {
      fetchCompanyDetails();
    }

    const subscription = ledgerStream.subscribe(() => {
      fetchEvents();
    });

    return () => subscription.unsubscribe();
  }, [tenantId]);

  const fetchCompanyDetails = async () => {
    if (!tenantId) return;
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('jurisdiction')
        .eq('id', tenantId)
        .single();

      if (error) throw error;
      setCompanyJurisdiction(data?.jurisdiction || 'US');
    } catch (error: any) {
      console.error('Error fetching company:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const data = await CapLedgerService.getLedgerEvents();
      setEvents(data);
    } catch (error: any) {
      toast({
        title: 'Error loading events',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Transaction Ledger</h1>
            <p className="text-muted-foreground">
              Complete audit trail of all share issuances, transfers, and conversions
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Record Event
          </Button>
        </div>

        {tenantId && <FundingRoundCard companyId={tenantId} />}

        {tenantId && <ScenarioWorkbench companyId={tenantId} />}

        {tenantId && <TransferDutyPanel companyId={tenantId} jurisdiction={companyJurisdiction} />}

        <LedgerEventList events={events} loading={loading} />

        <LedgerEventDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={fetchEvents} />
      </div>
    </DashboardLayout>
  );
};

export default Transactions;
